import { NOT_APPLICABLE_ANSWER } from 'schema/constants';
import type { Poll } from 'schema/poll';
import type { AnswerState, ICState } from 'schema/state';
import { InterpreterIntl } from '../../machine/interpreter';
import { selectQuestion, selectRelevantQuestions } from '../../selects';
import { findLastInStack, notApplicable, pushToStack } from '../../utils';
import { recalculateVisibleQuestions, shouldRecalculateVisibleQuestions } from '../utils/recalculateVisibleQuestions';
import { validateAnswersOnChange } from '../validate';

export interface ChangeActionParams {
  questionId: string;
  answeredIds: string[];
  answersOrder?: number[];
  touchedBy: NonNullable<AnswerState['lastTouchedBy']>;
}

export function change(
  data: Poll,
  prevState: ICState,
  { questionId, answeredIds, answersOrder, touchedBy }: ChangeActionParams,
  intl: InterpreterIntl,
): ICState {
  const question = selectQuestion(data, questionId);

  const isNotApplicable = notApplicable({ answeredIds });
  if (isNotApplicable) answeredIds = [NOT_APPLICABLE_ANSWER];

  const found = findLastInStack(prevState.answersStack, questionId);
  const answerState: typeof found = {
    ...found,
    lastTouchedBy: touchedBy,
    isTimeExpired: found.isTimeExpired === 'unchangedSinceExpired' ? 'changedSinceExpired' : found.isTimeExpired,
    isNotApplicable,
    answeredIds,
    ...(answersOrder && { answersOrder }),
  };

  const answersStack = pushToStack(prevState.answersStack, {
    ...answerState,
    errorMessage: validateAnswersOnChange(
      question,
      selectRelevantQuestions({ state: prevState, poll: data }),
      pushToStack(prevState.answersStack, answerState),
      intl,
    ),
  });

  let state = { ...prevState, answersStack };

  if (shouldRecalculateVisibleQuestions(state, data)) {
    state = recalculateVisibleQuestions(state, data);
  }

  if (state.startedAt == null) state.startedAt = new Date().getTime();
  return state;
}
